/**
 * Assignment Detail Component
 * Displays comprehensive information about an assignment
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Calendar, 
  DollarSign, 
  Clock, 
  FileText, 
  Download, 
  User, 
  BookOpen,
  MessageSquare,
  Eye,
  School,
  ArrowLeft,
  CheckCircle,
  AlertTriangle
} from 'lucide-react';
import { formatDistance } from 'date-fns';
import MessageButton from '../../../features/messaging/components/MessageButton'; // Fixed import

const AssignmentDetail = ({ 
  assignment, 
  isOwner = false,
  onComplete,
  isLoading,
  ownerProfile
}) => {
  if (!assignment) return null;
  
  // Format date
  const formatDate = (date) => {
    if (!date) return 'N/A';
    
    const d = new Date(date);
    return d.toLocaleDateString('en-ZA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };
  
  // Format relative time
  const formatRelativeTime = (date) => {
    if (!date) return 'Unknown time';
    return formatDistance(new Date(date), new Date(), { addSuffix: true });
  };
  
  // Get status badge color and text
  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return {
          color: 'bg-green-100 text-green-800',
          text: 'Open'
        };
      case 'assigned':
        return {
          color: 'bg-blue-100 text-blue-800',
          text: 'Assigned'
        };
      case 'completed':
        return {
          color: 'bg-purple-100 text-purple-800',
          text: 'Completed'
        };
      case 'cancelled':
        return {
          color: 'bg-red-100 text-red-800',
          text: 'Cancelled'
        };
      default:
        return {
          color: 'bg-gray-100 text-gray-800',
          text: 'Unknown'
        };
    }
  };
  
  // Check if assignment is due soon (within 2 days)
  const isDueSoon = () => {
    if (!assignment.dueDate) return false;
    
    const now = new Date();
    const dueDate = new Date(assignment.dueDate);
    const diffTime = dueDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays >= 0 && diffDays <= 2;
  };
  
  // Get days remaining text
  const getDaysRemaining = () => {
    if (!assignment.dueDate) return 'No due date';
    
    const now = new Date();
    const dueDate = new Date(assignment.dueDate);
    const diffTime = dueDate - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) {
      return 'Past due';
    } else if (diffDays === 0) {
      return 'Due today';
    } else if (diffDays === 1) {
      return 'Due tomorrow';
    } else {
      return `${diffDays} days left`;
    }
  };
  
  const statusBadge = getStatusBadge(assignment.status);
  const dueSoon = isDueSoon() && assignment.status === 'open';
  
  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
      {/* Back Button */}
      <div className="px-4 py-3 border-b border-gray-100">
        <Link to="/assignments" className="text-gray-600 hover:text-gray-800 flex items-center text-sm">
          <ArrowLeft size={16} className="mr-1" />
          Back to Assignments
        </Link>
      </div>
      
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="flex items-center mb-2">
            <span className={`text-xs px-2 py-1 rounded-full ${statusBadge.color} mr-2`}>
              {statusBadge.text}
            </span>
            
            {/* Due Date Badge */}
            {dueSoon && (
              <span className="text-xs px-2 py-1 rounded-full bg-red-100 text-red-800 flex items-center">
                <AlertTriangle size={12} className="mr-1" />
                {getDaysRemaining()}
              </span>
            )}
          </div>
          
          <h1 className="text-xl font-semibold text-gray-800 mb-1">
            {assignment.title}
          </h1>
          
          <div className="flex flex-wrap text-sm text-gray-600">
            <span className="mr-3 mb-1">{assignment.subject}</span>
            {assignment.course && (
              <>
                <span className="mr-3 mb-1">•</span>
                <span className="mr-3 mb-1">{assignment.course}</span>
              </>
            )}
            <span className="mr-3 mb-1">•</span>
            <span className="mb-1">{assignment.university}</span>
          </div>
        </div>
        
        <div className="flex items-center space-x-3 text-sm text-gray-500">
          <span className="flex items-center">
            <Eye size={16} className="mr-1" />
            {assignment.viewCount || 0} views
          </span>
          <span className="flex items-center">
            <MessageSquare size={16} className="mr-1" />
            {assignment.offerCount || 0} offers
          </span>
          <span className="flex items-center">
            <Calendar size={16} className="mr-1" />
            Posted {formatRelativeTime(assignment.createdAt)}
          </span>
        </div>
      </div>
      
      {/* Action Button - for Assignment Owner */}
      {isOwner && assignment.status === 'assigned' && (
        <div className="px-6 py-3 bg-blue-50 flex items-center justify-between">
          <div className="flex items-center text-blue-800">
            <AlertTriangle size={16} className="mr-2" />
            <span className="text-sm">
              Once you've received satisfactory help, mark this assignment as completed.
            </span>
          </div>
          <button
            onClick={onComplete}
            disabled={isLoading}
            className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white text-sm rounded-md transition flex items-center disabled:opacity-70 disabled:cursor-not-allowed"
          >
            {isLoading ? (
              <>
                <span className="inline-block w-4 h-4 border-2 border-t-transparent border-white rounded-full animate-spin mr-2"></span>
                Processing...
              </>
            ) : (
              <>
                <CheckCircle size={16} className="mr-2" />
                Mark Complete
              </>
            )}
          </button>
        </div>
      )}
      
      {/* Completed Message */}
      {assignment.status === 'completed' && (
        <div className="px-6 py-3 bg-green-50 flex items-center">
          <CheckCircle size={16} className="mr-2 text-green-600" />
          <span className="text-sm text-green-800">
            This assignment was marked as completed {assignment.completedAt ? formatRelativeTime(assignment.completedAt) : ''}.
          </span>
        </div>
      )}
      
      {/* Main Content */}
      <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left Column - Assignment Details */}
        <div className="md:col-span-2 space-y-6">
          {/* Description */}
          <div>
            <h2 className="text-lg font-medium text-gray-800 mb-3">
              Description
            </h2>
            <p className="text-gray-600 whitespace-pre-line">
              {assignment.description}
            </p>
          </div>
          
          {/* File Attachment */}
          <div>
            <h2 className="text-lg font-medium text-gray-800 mb-3">
              Attachment
            </h2>
            <a
              href={assignment.fileUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center p-4 border border-gray-200 rounded-md hover:bg-gray-50 transition"
            >
              <div className="w-10 h-10 bg-gray-100 rounded-md flex items-center justify-center mr-3">
                <FileText size={20} className="text-gray-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-800 truncate">
                  {assignment.fileName || 'Assignment File'}
                </p>
                <p className="text-xs text-gray-500">
                  {assignment.fileSize ? `${Math.round(assignment.fileSize / 1024)} KB` : ''} 
                  {assignment.fileType ? ` • ${assignment.fileType.split('/')[1].toUpperCase()}` : ''}
                </p>
              </div>
              <Download size={18} className="text-gray-500 ml-2" />
            </a>
          </div>
        </div>
        
        {/* Right Column - Sidebar */}
        <div className="space-y-6">
          {/* Key Details Card */}
          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
              <h3 className="text-sm font-medium text-gray-800">Assignment Details</h3>
            </div>
            <div className="divide-y divide-gray-100">
              {/* Due Date */}
              <div className="p-4 flex items-center">
                <Calendar size={18} className={`mr-3 ${dueSoon ? 'text-red-500' : 'text-gray-500'}`} />
                <div>
                  <p className="text-xs text-gray-500">Due Date</p>
                  <p className={`text-sm font-medium ${dueSoon ? 'text-red-600' : 'text-gray-800'}`}>
                    {formatDate(assignment.dueDate)} {!dueSoon && assignment.dueDate && (
                      <span className="text-xs font-normal text-gray-500">
                        ({getDaysRemaining()})
                      </span>
                    )}
                  </p>
                </div>
              </div>
              
              {/* Budget */}
              <div className="p-4 flex items-center">
                <DollarSign size={18} className="mr-3 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500">Budget</p>
                  <p className="text-sm font-medium text-gray-800">
                    R{assignment.budget}
                  </p>
                </div>
              </div>
              
              {/* Time Estimate */}
              <div className="p-4 flex items-center">
                <Clock size={18} className="mr-3 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500">Time Estimate</p>
                  <p className="text-sm font-medium text-gray-800">
                    {assignment.timeEstimate} hours
                  </p>
                </div>
              </div>
              
              {/* Year Level */}
              <div className="p-4 flex items-center">
                <BookOpen size={18} className="mr-3 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500">Year Level</p>
                  <p className="text-sm font-medium text-gray-800">
                    {assignment.yearLevel || 'Not specified'}
                  </p>
                </div>
              </div>
              
              {/* University */}
              <div className="p-4 flex items-center">
                <School size={18} className="mr-3 text-gray-500" />
                <div>
                  <p className="text-xs text-gray-500">University</p>
                  <p className="text-sm font-medium text-gray-800">
                    {assignment.university}
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Assignment Owner Card */}
          {ownerProfile && !isOwner && (
            <div className="border border-gray-200 rounded-lg overflow-hidden">
              <div className="px-4 py-3 bg-gray-50 border-b border-gray-200">
                <h3 className="text-sm font-medium text-gray-800">Posted By</h3>
              </div>
              <div className="p-4">
                <div className="flex items-center mb-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden mr-3">
                    {ownerProfile.photos && ownerProfile.photos.length > 0 ? (
                      <img 
                        src={ownerProfile.photos[0]} 
                        alt={ownerProfile.basicInfo?.name || 'User'} 
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-gray-100 text-gray-500">
                        <User size={18} />
                      </div>
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-gray-800">
                      {ownerProfile.basicInfo?.name || 'Anonymous User'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {ownerProfile.academic?.university || 'University not specified'}
                    </p>
                  </div>
                </div>
                
                {/* Message Button */}
                <MessageButton
                  userId={assignment.userId}
                  className="w-full py-2 px-3 rounded-md"
                  variant="primary"
                  showText={true}
                />
              </div>
            </div>
          )}
          
          {/* Academic Integrity Card */}
          <div className="border border-yellow-200 rounded-lg bg-yellow-50 p-4">
            <h3 className="text-sm font-medium text-yellow-800 mb-2 flex items-center">
              <AlertTriangle size={16} className="mr-2" />
              Academic Integrity
            </h3>
            <p className="text-xs text-yellow-700">
              VarsityLink is committed to upholding academic integrity. All assistance should comply with your institution's academic policies. Tutors provide guidance only, not completed work for submission.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssignmentDetail;